import { useFormikContext } from 'formik';
import React from 'react';
import _ from 'underscore';

import { Messages } from '../../../../../codegen/Localize';
import WebHttpClient from '../../../../../httpClient/WebHttpClient';
import { L10n } from '../../../../../ts/L10n/L10n';
import { GetNumExcessRevisionsRequest, IGetNumExcessRevisionsParams, IGetNumExcessRevisionsResult } from '../../../../../ts/ServerApi/requests/GetNumExcessRevisionsRequest';
import { useCompositeValidator, usePositiveIntegerValidator, useRangeValidator, useValidatorWithDisable } from '../../../utils';
import { InputDebounceMs } from '../../GeneralSettingsPageBase';
import { RevisionHistorySectionProps } from './RevisionHistorySection';

export namespace RevisionHistoryBusinessLogic {
  export function useRevisionsValidator(disabled: boolean) {
    return useValidatorWithDisable(disabled,
      useCompositeValidator(
        usePositiveIntegerValidator(Messages.contentVersionLimitMustBePositiveNumber),
        useRangeValidator(
          2, Messages.contentVersionLimitMustBePositiveNumber,
          10000, Messages.contentVersionLimitMustBePositiveNumber)
      )
    );
  }

  export function useDebouncedExcessRevisionsMessageUpdater(setter: (value: string) => void) {
    const formik = useFormikContext<RevisionHistorySectionProps>();
    const flowsEnabled = formik.initialValues.settings.flowsOnSiteEnabled;
    const error = formik?.errors?.settings?.contentVersionLimit;

    // This code was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return React.useCallback(_.debounce((limit: number) => {
      // In certain cases where the limit isn't set, don't make an API call
      // as it requires a limit parameter and will return an error.
      if (!error && limit) {
        RevisionHistoryBusinessLogic.getExcessRevisionsMessageFromServer(limit, flowsEnabled)
          .then(setter);
      }
    }, InputDebounceMs), [flowsEnabled, error]);
  }

  export function getExcessRevisionsMessageFromServer(newLimit: number, flowsOnSiteEnabled: boolean): Promise<string> {
    const params: IGetNumExcessRevisionsParams = {
      limit: newLimit
    };

    // This code contains a bug, because the getNumExcessRevisions request is implicitly site-scoped,
    // but this UI can show up in the edit site settings dialog from the all sites list.
    // The effect is that we'll fetch the excess revisions info for whatever site the
    // server admin is signed into regardless of which site they're editing.
    // TFS: 815654
    const promise = WebHttpClient.sendRequest(new GetNumExcessRevisionsRequest(params))
      .then((result: IGetNumExcessRevisionsResult) => {
        // there was an if statement here in the original logic. It looks like it's comparing the value to itself
        // so it's always true. I'll need to verify further
        const workbookRevisionCount = result.workbookRevisionCount;
        const datasourceRevisionCount = result.datasourceRevisionCount;
        const flowRevisionCount = result.flowRevisionCount;

        if (flowsOnSiteEnabled &&
          (flowRevisionCount > 0 || workbookRevisionCount > 0 || datasourceRevisionCount > 0)) {
          return Messages.contentVersionLimitWarningWithWorkbookDataSourceAndFlowValues({
            LIMIT_COUNT: L10n.localizedCount(newLimit),
            EXCESS_WORKBOOK_COUNT: L10n.localizedCount(workbookRevisionCount),
            EXCESS_DATASOURCE_COUNT: L10n.localizedCount(datasourceRevisionCount),
            EXCESS_FLOW_COUNT: L10n.localizedCount(flowRevisionCount)
          });
        } else if (workbookRevisionCount > 0 || datasourceRevisionCount > 0) {
          return Messages.contentVersionLimitWarningWithWorkbookAndDataSourceValues({
            LIMIT_COUNT: L10n.localizedCount(newLimit),
            EXCESS_WORKBOOK_COUNT: L10n.localizedCount(workbookRevisionCount),
            EXCESS_DATASOURCE_COUNT: L10n.localizedCount(datasourceRevisionCount)
          });
        }
      });

    return Promise.resolve(promise);
  }
}
